<template>
  <m-content
    class="invite-users"
    padding
    :padding-top="0"
  >
    <div class="_description">
      {{ $t('inviteUsers.description') }} <span v-if="withInvite">{{ $t('inviteUsers.descriptionInvite') }}</span>
    </div>
    <div class="_item">
      <div class="_label">
        {{ $t('inviteUsers.inviteUsersLabel') }}
      </div>
      <m-tooltip :disabled="!wouldExceedUserLimit(emails.length + 1)">
        <div class="_email-input">
          <m-input
            v-model:value="email"
            full-width
            class="_input"
            :placeholder="$t('inviteUsers.inviteUsersPlaceholder')"
            :has-error="inputError !== ''"
            @focus="emailFocused = true"
            @blur="emailFocused = false"
            @enter="addEmail"
          />
          <m-btn
            color="primary"
            :disabled="wouldExceedUserLimit(emails.length + 1)"
            @click="addEmail"
          >
            {{ $t('inviteUsers.addEmail') }}
          </m-btn>
        </div>
        <template #title>
          {{ $t('userList.trialUserLimitReached', { limit: userLimitValue }) }}
        </template>
      </m-tooltip>
      <div
        v-if="inputError !== ''"
        class="_error-message"
      >
        {{ inputError }}
      </div>
      <div
        v-if="emails.length > 0"
        class="_email-list"
      >
        <m-tag
          v-for="e in emails"
          :key="e"
          :title="e"
          closeable
          @close="removeEmail(e)"
        />
      </div>
    </div>
    <div class="_item">
      <div class="_label">
        {{ $t('inviteUsers.spaceLabel') }}
      </div>
      <space-selector
        v-model:value="selectedSpaces"
        class="_select"
        :property-label="spaceProperty.label"
        :truncate="5"
        :with-archived="false"
        :placeholder="$t('inviteUsers.spacePlaceholder')"
        placement="onTopLeft"
        match-trigger-width
        full-width
        multiple
        wrap
      />
    </div>
    <div class="_item">
      <div class="_label">
        {{ $t('inviteUsers.languageLabel') }}
      </div>
      <m-lang-picker
        v-model:value="language"
        class="_select"
        :included-language-codes="AVAILABLE_SYSTEM_LANGUAGE_CODES"
        match-trigger-width
        full-width
      />
      <div class="_hint">
        {{ $t('inviteUsers.languageHint') }}
      </div>
    </div>
    <div
      v-if="accessGroupItems.length > 0"
      class="_item"
    >
      <div class="_label">
        {{ $t('inviteUsers.accessGroupLabel') }}
      </div>
      <m-select
        v-model:value="selectedAccessGroup"
        class="_select"
        :items="accessGroupItems"
        :placeholder="$t('inviteUsers.accessGroupPlaceholder')"
        match-trigger-width
        full-width
        clearable
      />
    </div>
    <div
      v-if="canSkipInviteEmail"
      class="_item"
    >
      <m-switch
        v-model:value="sendInviteEmail"
        :label="$t('inviteUsers.sendInviteEmailLabel')"
      />
      <div class="_hint">
        {{ $t('inviteUsers.sendInviteEmailHint') }}
      </div>
    </div>
    <m-alert
      v-if="userLimitReached"
      type="info"
      :message="$t('userList.trialUserLimitReached', { limit: userLimitValue })"
    />
    <div class="_actions">
      <m-btn
        block
        :loading="loading"
        :disabled="emails.length === 0 || wouldExceedUserLimit(emails.length)"
        color="primary"
        @click="sendInvites"
      >
        {{ sendInviteEmail ? $t('inviteUsers.sendInvite') : $t('inviteUsers.addUsers') }}
      </m-btn>
      <m-btn
        full-width
        @click="emit('close')"
      >
        {{ $t('general.cancel') }}
      </m-btn>
    </div>
  </m-content>
</template>

<script setup>
import MLangPicker from '@/components/MLangPicker.vue';
import SpaceSelector from '@/components/spaces/SpaceSelector.vue';
import useAccessGroup from '@/composables/access/access-group';
import useAccountSettings from '@/composables/logged-in-user-account/account-settings';
import useInvitations from '@/composables/user/invitations/invitations';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useOnboardingMarker from '@/composables/onboarding/onboarding-marker';
import useProperties from '@/composables/property/property';
import useSnackbar from '@/composables/snackbar';
import useUserLimit from '@/composables/user/user-limit';
import useUsers from '@/composables/user/users';
import { AVAILABLE_SYSTEM_LANGUAGE_CODES } from '@/lib/language';
import { accessPolicyType, failureCode, onboardingGuideSteps } from 'shared/constants.json';
import { computed, ref, watch } from 'vue';
import { copy } from 'shared/lib/copy';
import { emailRegex } from 'shared/lib/email-regex';
import { failureCodeOf, failureOf, logCatch } from '@/lib/logger/logger';
import { makeStaticUserUserScope, makeUserScopeTree } from '@/lib/user-scope-tree';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const snackbar = useSnackbar();
const emit = defineEmits(['close']);

const props = defineProps({ canSkipInviteEmail: { type: Boolean, default: false } });

const { loggedInUserAccount } = useLoggedInUserAccount();
const { userLang } = useLoggedInUser();
const accountSettingsSvc = useAccountSettings();
const { spaceProperty } = useProperties();
const { markAsDone } = useOnboardingMarker(loggedInUserAccount, accountSettingsSvc);
const { createUsers, users, archivedUsers } = useUsers();
const { inviteUsers } = useInvitations();
const { userLimitValue, userLimitReached, wouldExceedUserLimit } = useUserLimit();
const accessGroupSvc = useAccessGroup();

const email = ref('');
const emails = ref([]);
const selectedSpaces = ref([]);
const selectedAccessGroup = ref(null);
const sendInviteEmail = ref(true);
const loading = ref(false);
const language = ref(userLang.value);

const accessGroupItems = computed(() => accessGroupSvc.accessGroups.value
  .filter((ag) => ag.isDefaultGroup !== true)
  .map((ag) => ({ value: ag.uid, text: ag.name })));
const emailFocused = ref(false);
const submitPressed = ref(false);

const isValidEmail = computed(() => {
  const rg = new RegExp(emailRegex());
  return rg.test(email.value);
});

const alreadyAdded = computed(() => emails.value.includes(email.value));

const accountEmails = computed(() => new Set([...users.value, ...archivedUsers.value]
  .map((u) => u.identity?.email?.toLowerCase())
  .filter((e) => e !== undefined && e !== '')));
const alreadyInAccount = computed(() => accountEmails.value.has(email.value.toLowerCase()));

const errorMessage = computed(() => {
  if (!isValidEmail.value) {
    return t('inviteUsers.invalidEmail');
  }
  if (alreadyAdded.value) {
    return t('inviteUsers.alreadyAdded');
  }
  if (alreadyInAccount.value) {
    return t('inviteUsers.emailAlreadyInAccount');
  }
  return '';
});

const inputError = computed(() => {
  if (!submitPressed.value && (email.value === '' || emailFocused.value)) {
    return '';
  }
  return errorMessage.value;
});

watch(email, () => {
  submitPressed.value = false;
});

const addEmail = () => {
  if (wouldExceedUserLimit(emails.value.length + 1)) {
    return;
  }
  submitPressed.value = true;
  if (errorMessage.value !== '') {
    return;
  }
  emails.value = [...emails.value, email.value];
  email.value = '';
};

const removeEmail = (emailToRemove) => {
  emails.value = emails.value.filter((e) => e !== emailToRemove);
};

const parseDuplicateEmailError = (message) => {
  const result = [];
  const lines = message.split('\n');
  lines.forEach((line) => {
    const s1 = 'user with email ';
    const s2 = ' already exists';
    const i1 = line.indexOf(s1);
    const i2 = line.indexOf(s2);
    if (i1 > -1 && i2 > -1) {
      result.push(line.substring(i1 + s1.length, i2));
    }
  });
  return result;
};

const assignAccessGroup = (createdUsers) => {
  const group = accessGroupSvc.accessGroups.value.find((ag) => ag.uid === selectedAccessGroup.value);
  if (group === undefined || group.accessPolicy === undefined) {
    return Promise.resolve();
  }

  const accountRef = { uid: loggedInUserAccount.value.uid };
  const newScopes = createdUsers.map((u) => ({
    accessType: accessPolicyType.read,
    scope: makeUserScopeTree(accountRef, makeStaticUserUserScope({ uid: u.uid })),
    scopeUsers: [{ uid: u.uid }],
  }));

  // Partial updates fail validation; optimistic: false keeps the half-formed
  // scopes (no uid/userIsInScope yet) out of the store until the server responds.
  return accessGroupSvc.updateSingle({
    ...copy(group),
    accessPolicy: {
      ...copy(group.accessPolicy),
      scopes: [...group.accessPolicy.scopes, ...newScopes],
    },
  }, { optimistic: false });
};

const withInvite = computed(() => !props.canSkipInviteEmail || sendInviteEmail.value);
const sendInvites = () => {
  if (wouldExceedUserLimit(emails.value.length)) {
    snackbar.error(t('userList.trialUserLimitReached', { limit: userLimitValue.value }));
    return;
  }
  loading.value = true;
  const toCreate = emails.value.map((e) => ({
    identity: {
      email: e,
      language: language.value,
    },
    values: [
      {
        property: { uid: spaceProperty.value.uid },
        spaces: selectedSpaces.value.map((o) => ({ uid: o.uid })),
      },
    ],
  }));
  createUsers(toCreate)
    .then((createdUsers) => (withInvite.value ? inviteUsers(createdUsers) : Promise.resolve())
      .then(() => assignAccessGroup(createdUsers).catch(() => {
        snackbar.error(t('inviteUsers.accessGroupAssignFailed'));
      })))
    .then(() => (withInvite.value ? markAsDone([onboardingGuideSteps.inviteOthers]) : undefined))
    .then(() => {
      snackbar.success(withInvite.value ? t('inviteUsers.success') : t('inviteUsers.usersAddedSuccess'));
      emit('close');
    })
    .catch(logCatch((e) => {
      if (failureCodeOf(e) === failureCode.emailExists) {
        const duplicateEmails = parseDuplicateEmailError(failureOf(e).message);
        snackbar.error(t('inviteUsers.oneEmailAlreadyExists'), { description: `- ${duplicateEmails.join(', ')}`, duration: 30 });
      } else {
        snackbar.error();
      }
      loading.value = false;
    }, [failureCode.emailExists]));
};
</script>

<style scoped lang="scss" type="text/scss">
  .invite-users {
    ._description {
      color: $font-color-secondary;
      margin-bottom: 1.6rem;
    }

    ._item {
      margin-bottom: 2rem;

      ._label {
        color: $font-color-secondary;
        margin-bottom: .4rem;
      }

      ._hint {
        color: $font-color-secondary;
        font-size: $font-size-2;
        margin-top: .4rem;
      }
    }

    ._email-input {
      display: flex;
      gap: .8rem;

      ._input {
        flex: 1;
      }
    }

    ._error-message {
      color: $error-color;
      margin-top: .4rem;
    }

    ._email-list {
      display: flex;
      flex-wrap: wrap;
      gap: .4rem;
      max-height: 12rem;
      margin-top: .8rem;
      overflow-y: auto;
    }

    ._actions {
      display: flex;
      flex-direction: column;
      gap: .8rem;
      margin-top: 2rem;
    }
  }
</style>
